import React, { forwardRef, useEffect, useId, useMemo, useRef } from 'react';
import {
  Box,
  PlacementWithLogical,
  Portal,
  useMergeRefs,
  usePopper,
  Flex,
} from '@chakra-ui/react';
import { callAllHandlers } from '@allergan-data-labs/alle-elements-core';
import { ChevronRightIcon } from '@allergan-data-labs/alle-elements-icons/20/chevronRightIcon';
import { ActionMenu, type ActionMenuProps } from './action-menu';
import { ActionListItem, type ActionListItemProps } from './action-list-item';
import {
  ActionSubMenuProvider,
  useActionSubMenu,
  useActionSubMenuContext,
} from './action-sub-menu.hook';
import { getActionSubmenuTriggerStyles } from './action-sub-menu.theme';
import {
  ActionMenuContent,
  type ActionMenuContentProps,
} from './action-menu-content';

export type ActionSubMenuRootProps = Omit<ActionMenuProps, 'closeOnBlur'>;

export const ActionSubMenuRoot = ({
  children,
  closeOnSelect = true,
  ...rest
}: ActionSubMenuRootProps) => {
  const [isRootOpen, setIsRootOpen] = React.useState<boolean>(false);
  const [submenuItems, setSubmenuItems] = React.useState<
    HTMLButtonElement[] & HTMLDivElement[]
  >([]);
  const rootMenuRef = useRef<HTMLDivElement>(null);
  const value = useMemo(
    () => ({
      isRootOpen,
      closeOnSelect,
      rootMenuRef,
      submenuItems,
      setIsRootOpen,
      setSubmenuItems,
    }),
    [isRootOpen, closeOnSelect, submenuItems]
  );

  return (
    <ActionSubMenuProvider value={value}>
      <ActionMenu
        isOpen={isRootOpen}
        onOpen={() => {
          setIsRootOpen(true);
        }}
        onClose={() => {
          setIsRootOpen(false);
        }}
        closeOnSelect={false}
        closeOnBlur={false}
        {...rest}
      >
        <Box ref={rootMenuRef}>{children}</Box>
      </ActionMenu>
    </ActionSubMenuProvider>
  );
};

export interface ActionSubMenuProps extends ActionMenuProps {
  /**
   * The placement of the Action Menu relative to its reference.
   * @default end-start
   */
  placement?: PlacementWithLogical;
  /**
   * The label of the Action Sub Menu Trigger button.
   */
  label: string;
  /**
   * Props to pass to the Action Sub Menu.
   */
  menuProps?: ActionMenuProps;
  /**
   * Props to pass to the Action Sub Menu Trigger button.
   */
  submenuTriggerProps?: ActionListItemProps;
}

export const ActionSubMenu = ({
  children,
  label,
  menuProps,
  submenuTriggerProps,
  placement = 'end-start',
}: ActionSubMenuProps) => {
  const submenuId = `submenu-${useId()}`;
  const {
    colorMode,
    submenuRef,
    menuTriggerItemRef,
    isSubmenuOpen,
    submenuItems,
    onKeyDownHandler,
    onCloseHandler,
    setSubmenuItems,
    onSubmenuClose,
    onMouseLeave,
    onMouseEnter,
  } = useActionSubMenu();
  const { popperRef, referenceRef, getPopperProps } = usePopper({
    placement,
    offset: [-8, -8],
  });

  const onMouseLeaveSubmenuHandler = () => {
    onSubmenuClose();
  };

  useEffect(() => {
    if (menuTriggerItemRef?.current) {
      submenuItems.push(menuTriggerItemRef.current);
      setSubmenuItems(submenuItems);
    }
  }, [menuTriggerItemRef, setSubmenuItems, submenuItems]);

  const onSubmenuTriggerHandler = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    return false;
  };

  return (
    <>
      <ActionListItem
        {...submenuTriggerProps}
        ref={useMergeRefs(referenceRef, menuTriggerItemRef)}
        onClick={callAllHandlers(
          submenuTriggerProps?.onClick,
          onSubmenuTriggerHandler
        )}
        onKeyDownCapture={callAllHandlers(
          submenuTriggerProps?.onKeyDownCapture,
          onKeyDownHandler
        )}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        sx={{
          ...getActionSubmenuTriggerStyles(colorMode, isSubmenuOpen),
          ...submenuTriggerProps?.sx,
        }}
        rightContent={<ChevronRightIcon />}
      >
        <Flex>{label}</Flex>
      </ActionListItem>
      <Portal>
        <Box ref={popperRef} {...getPopperProps({ style: { zIndex: 2 } })}>
          <ActionMenu
            id={submenuId}
            isOpen={isSubmenuOpen}
            isSubmenu={true}
            onClose={onCloseHandler}
            closeOnSelect={false}
            colorMode={colorMode}
            {...menuProps}
            closeOnBlur={false}
          >
            <ActionSubMenuContent
              ref={submenuRef}
              onMouseLeave={onMouseLeaveSubmenuHandler}
              onKeyDown={onKeyDownHandler}
            >
              {children}
            </ActionSubMenuContent>
          </ActionMenu>
        </Box>
      </Portal>
    </>
  );
};

export type ActionSubMenuContentProps = ActionMenuContentProps;

export const ActionSubMenuContent = forwardRef(
  (props: ActionSubMenuContentProps, ref) => {
    return (
      <ActionMenuContent
        data-testid="actionSubMenuContent"
        ref={ref}
        {...props}
      />
    );
  }
);

export type ActionSubMenuListItemProps = ActionListItemProps;

export const ActionSubMenuListItem = forwardRef<
  HTMLButtonElement,
  ActionSubMenuListItemProps
>((props: ActionSubMenuListItemProps, ref) => {
  const { setIsRootOpen, closeOnSelect, submenuItems, setSubmenuItems } =
    useActionSubMenuContext();
  const localRef = useRef<HTMLButtonElement>(null);
  const onClickHandler = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setIsRootOpen(!closeOnSelect);
  };

  const onKeyDownHandler = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    setIsRootOpen(false);
  };

  useEffect(() => {
    if (localRef && localRef?.current) {
      submenuItems.push(localRef.current);
      setSubmenuItems(submenuItems);
    }
  }, [setSubmenuItems, submenuItems]);

  return (
    <ActionListItem
      {...props}
      ref={useMergeRefs(localRef, ref)}
      onClick={callAllHandlers(props.onClick, onClickHandler)}
      onKeyDownCapture={callAllHandlers(
        props.onKeyDownCapture,
        onKeyDownHandler
      )}
    />
  );
});
